<template>
	<div>
		<div v-if="loading" class="text-center py-5">
			<div class="spinner-border" role="status"></div>
		</div>

		<main v-else-if="config && draft" class="container-xl py-3">

			<!-- Owner-controls notice strip -->
			<div class="rounded mb-3 px-3 py-2" style="background:#fef3c7;border:1px solid #fcd34d;font-size:12px;color:#78350f;">
				<span class="fw-semibold">Owner controls · Role-gating coming soon.</span>
				Changes here affect every bundle quote, every Quote Builder line item, and every proposal comparison across the app.
			</div>

			<!-- Pricing-formula description card -->
			<div class="card shadow-sm mb-3">
				<div class="card-body p-3">
					<h2 class="h6 fw-semibold text-dark mb-1">Pricing formula</h2>
					<p class="mb-0" style="font-size:12px;color:#64748b;">
						Base formula: Setup = <span class="font-monospace">{{ config.formula.setup.description }}</span>
						· Annual = <span class="font-monospace">{{ config.formula.annual.description }}</span>.
						Bundles multiply this base; state tier / SNAP / trend adjust it per-muni.
					</p>
				</div>
			</div>

			<!-- ─── Owner controls · pricing formula ─────────────────────── -->
			<section class="rounded shadow-sm mb-4" style="border:2px solid #fcd34d;background:rgba(254,243,199,0.4);">
				<header class="px-3 py-2 d-flex align-items-baseline justify-content-between gap-3"
					style="background:#fde68a;border-bottom:1px solid #fcd34d;border-top-left-radius:0.375rem;border-top-right-radius:0.375rem;">
					<div class="min-w-0">
						<h2 class="h6 fw-semibold mb-0 d-flex align-items-baseline gap-2" style="color:#78350f;">
							<span class="d-inline-block rounded-circle" style="width:8px;height:8px;background:#b45309;"></span>
							Owner controls · pricing formula
						</h2>
						<p class="mb-0 mt-1" style="font-size:11px;color:rgba(120,53,15,0.8);">
							Role-gating coming soon — treat as owner-only. Changes apply on save.
						</p>
					</div>
					<div class="d-flex align-items-center gap-2 flex-shrink-0">
						<span v-if="status" class="fw-medium" style="font-size:12px;"
							:style="{ color: statusTone === 'ok' ? '#047857' : '#be123c' }">
							{{ status }}
						</span>
						<button v-if="!collapsed" type="button" @click="onReset" :disabled="saving"
							class="btn btn-sm" style="border:1px solid #f59e0b;background:white;color:#78350f;font-size:12px;padding:4px 10px;">
							Reset all
						</button>
						<button v-if="!collapsed" type="button" @click="onSave" :disabled="saving"
							class="btn btn-sm fw-medium text-white" style="background:#b45309;font-size:12px;padding:4px 12px;">
							{{ saving ? 'Saving…' : 'Save changes' }}
						</button>
						<button type="button" @click="collapsed = !collapsed"
							class="btn btn-sm" style="border:1px solid #f59e0b;background:white;color:#78350f;font-size:12px;padding:4px 8px;"
							:title="collapsed ? 'Show owner controls' : 'Collapse owner controls'">
							{{ collapsed ? '▸ Expand' : '▾ Collapse' }}
						</button>
					</div>
				</header>

				<div v-if="!collapsed" class="p-3">

					<!-- Plain-English summary -->
					<div class="rounded p-2 mb-3" style="background:white;border:1px solid #fde68a;font-size:12px;line-height:1.55;color:#334155;">
						<div class="fw-semibold text-uppercase mb-1" style="font-size:10px;letter-spacing:0.05em;color:#b45309;">In plain English</div>
						{{ wholeFormulaSummary }}
					</div>

					<!-- ① Setup fee -->
					<div class="rounded bg-white p-3 mb-3" style="border:1px solid #fde68a;">
						<div class="d-flex align-items-baseline justify-content-between gap-2 mb-2">
							<div class="d-flex align-items-baseline gap-2 min-w-0">
								<span class="rounded fw-semibold text-uppercase px-2 py-1" style="font-size:10px;letter-spacing:0.05em;background:#fef3c7;color:#92400e;">①</span>
								<h3 class="h6 fw-semibold mb-0 text-truncate">Setup fee (one-time)</h3>
							</div>
						</div>
						<p class="mb-2" style="font-size:11px;color:#64748b;">Charged once at signup — differentiator vs. no-setup competitors</p>
						<p class="rounded mb-2 px-2 py-2" style="font-size:12px;line-height:1.4;color:#334155;background:#f8fafc;">{{ setupSummary }}</p>
						<div class="row g-3">
							<div class="col-12 col-sm-6">
								<label class="form-label mb-1" style="font-size:12px;font-weight:500;color:#334155;">Fixed setup</label>
								<div class="d-flex align-items-center gap-2">
									<span style="font-size:14px;color:#64748b;">$</span>
									<input type="number" min="0" step="50" class="form-control form-control-sm font-monospace text-end" v-model.number="draft.setupFixedDollars" />
									<button v-if="defaults && draft.setupFixedDollars !== defaults.setupFixedDollars" type="button" class="btn btn-sm btn-link p-0" style="color:#94a3b8;font-size:12px;width:20px;"
										@click="draft.setupFixedDollars = defaults.setupFixedDollars" :title="`Reset to $${defaults.setupFixedDollars}`">↺</button>
									<span v-else style="width:20px;"></span>
								</div>
								<span style="font-size:10px;color:#64748b;">Flat charge, any population.</span>
							</div>
							<div class="col-12 col-sm-6">
								<label class="form-label mb-1" style="font-size:12px;font-weight:500;color:#334155;">Per resident</label>
								<div class="d-flex align-items-center gap-2">
									<span style="font-size:14px;color:#64748b;">¢</span>
									<input type="number" min="0" step="2" class="form-control form-control-sm font-monospace text-end" v-model.number="draft.setupPerPopCents" />
									<button v-if="defaults && draft.setupPerPopCents !== defaults.setupPerPopCents" type="button" class="btn btn-sm btn-link p-0" style="color:#94a3b8;font-size:12px;width:20px;"
										@click="draft.setupPerPopCents = defaults.setupPerPopCents" :title="`Reset to ${defaults.setupPerPopCents}¢`">↺</button>
									<span v-else style="width:20px;"></span>
								</div>
								<span style="font-size:10px;color:#64748b;">×population, added to fixed setup.</span>
							</div>
						</div>
						<div v-if="impactPreview" class="pt-2 mt-2 d-flex align-items-baseline justify-content-between"
							style="border-top:1px solid #f1f5f9;font-size:11px;">
							<span style="color:#64748b;">Current impact <span style="color:#94a3b8;">· Bundle 1 at pop 5,000</span></span>
							<span class="font-monospace fw-semibold" style="color:#0f172a;">{{ fmtImpact(impactPreview.setupCents) }}</span>
						</div>
					</div>

					<!-- ② Annual hosting -->
					<div class="rounded bg-white p-3 mb-3" style="border:1px solid #a7f3d0;">
						<div class="d-flex align-items-baseline justify-content-between gap-2 mb-2">
							<div class="d-flex align-items-baseline gap-2 min-w-0">
								<span class="rounded fw-semibold text-uppercase px-2 py-1" style="font-size:10px;letter-spacing:0.05em;background:#d1fae5;color:#065f46;">②</span>
								<h3 class="h6 fw-semibold mb-0 text-truncate">Annual hosting</h3>
							</div>
						</div>
						<p class="mb-2" style="font-size:11px;color:#64748b;">Recurring — the number a rep quotes against competitors</p>
						<p class="rounded mb-2 px-2 py-2" style="font-size:12px;line-height:1.4;color:#334155;background:#f8fafc;">{{ annualSummary }}</p>
						<div class="row g-3">
							<div class="col-12 col-sm-6">
								<label class="form-label mb-1" style="font-size:12px;font-weight:500;color:#334155;">Fixed annual</label>
								<div class="d-flex align-items-center gap-2">
									<span style="font-size:14px;color:#64748b;">$</span>
									<input type="number" min="0" step="50" class="form-control form-control-sm font-monospace text-end" v-model.number="draft.annualFixedDollars" />
									<button v-if="defaults && draft.annualFixedDollars !== defaults.annualFixedDollars" type="button" class="btn btn-sm btn-link p-0" style="color:#94a3b8;font-size:12px;width:20px;"
										@click="draft.annualFixedDollars = defaults.annualFixedDollars" :title="`Reset to $${defaults.annualFixedDollars}`">↺</button>
									<span v-else style="width:20px;"></span>
								</div>
								<span style="font-size:10px;color:#64748b;">Flat yearly charge.</span>
							</div>
							<div class="col-12 col-sm-6">
								<label class="form-label mb-1" style="font-size:12px;font-weight:500;color:#334155;">Per resident</label>
								<div class="d-flex align-items-center gap-2">
									<span style="font-size:14px;color:#64748b;">¢</span>
									<input type="number" min="0" step="2" class="form-control form-control-sm font-monospace text-end" v-model.number="draft.annualPerPopCents" />
									<button v-if="defaults && draft.annualPerPopCents !== defaults.annualPerPopCents" type="button" class="btn btn-sm btn-link p-0" style="color:#94a3b8;font-size:12px;width:20px;"
										@click="draft.annualPerPopCents = defaults.annualPerPopCents" :title="`Reset to ${defaults.annualPerPopCents}¢`">↺</button>
									<span v-else style="width:20px;"></span>
								</div>
								<span style="font-size:10px;color:#64748b;">×population, added to fixed annual.</span>
							</div>
						</div>
						<div v-if="impactPreview" class="pt-2 mt-2 d-flex align-items-baseline justify-content-between"
							style="border-top:1px solid #f1f5f9;font-size:11px;">
							<span style="color:#64748b;">Current impact <span style="color:#94a3b8;">· Bundle 1 at pop 5,000</span></span>
							<span class="font-monospace fw-semibold" style="color:#0f172a;">{{ fmtImpact(impactPreview.annualCents) }}/yr</span>
						</div>
					</div>

					<!-- Global markup -->
					<div class="rounded bg-white p-3 mb-3" style="border:1px solid #e2e8f0;">
						<div class="d-flex align-items-baseline justify-content-between gap-2 mb-2">
							<h3 class="h6 fw-semibold mb-0 text-truncate">Global markup</h3>
							<button v-if="draft.baseMarkupDisplay !== 0" type="button" @click="draft.baseMarkupDisplay = 0"
								class="btn btn-link p-0" style="font-size:10px;color:#94a3b8;text-decoration:none;">Zero out</button>
						</div>
						<p class="mb-2" style="font-size:11px;color:#64748b;">Optional — lifts or drops every setup AND annual by the same percent</p>
						<p class="rounded mb-2 px-2 py-2" style="font-size:12px;line-height:1.4;color:#334155;background:#f8fafc;">{{ markupSummary }}</p>
						<div class="d-flex align-items-center gap-2">
							<input type="range" min="-25" max="50" step="0.5" v-model.number="draft.baseMarkupDisplay" class="form-range flex-grow-1" style="accent-color:#b45309;" />
							<input type="number" min="-25" max="50" step="0.5" v-model.number="draft.baseMarkupDisplay" class="form-control form-control-sm font-monospace text-end" style="width:80px;" />
							<span style="font-size:12px;color:#64748b;">%</span>
							<button v-if="defaults && draft.baseMarkupDisplay !== defaults.baseMarkupDisplay" type="button" class="btn btn-sm btn-link p-0" style="color:#94a3b8;font-size:12px;width:20px;"
								@click="draft.baseMarkupDisplay = defaults.baseMarkupDisplay">↺</button>
							<span v-else style="width:20px;"></span>
						</div>
					</div>

					<!-- ③ Population trend -->
					<div class="rounded bg-white p-3 mb-3" style="border:1px solid #a7f3d0;">
						<div class="d-flex align-items-baseline justify-content-between gap-2 mb-2">
							<div class="d-flex align-items-baseline gap-2 min-w-0">
								<span class="rounded fw-semibold text-uppercase px-2 py-1" style="font-size:10px;letter-spacing:0.05em;background:#d1fae5;color:#065f46;">③</span>
								<h3 class="h6 fw-semibold mb-0 text-truncate">Population trend adjustment</h3>
							</div>
							<button v-if="canZeroPopulation" type="button" @click="zeroPopulation"
								class="btn btn-link p-0" style="font-size:10px;color:#94a3b8;text-decoration:none;">Zero out</button>
						</div>
						<p class="mb-2" style="font-size:11px;color:#64748b;">How a muni's 5-year population change moves the price</p>
						<p class="rounded mb-2 px-2 py-2" style="font-size:12px;line-height:1.4;color:#334155;background:#f8fafc;">{{ populationSummary }}</p>

						<div class="row g-2 mb-2">
							<div class="col-6">
								<label class="rounded px-2 py-2 d-block" style="cursor:pointer;font-size:11px;"
									:style="draft.populationAdjustmentMode === 'regional'
										? { border:'1px solid #f59e0b', background:'#fef3c7', color:'#78350f', fontWeight:500 }
										: { border:'1px solid #cbd5e1', color:'#64748b' }">
									<input type="radio" class="me-1" value="regional" v-model="draft.populationAdjustmentMode" />
									Regional comparison
									<span class="d-block mt-1" style="font-size:10px;color:#64748b;line-height:1.35;font-weight:400;">
										Muni beats / lags BOTH peers → flat ±X%.
									</span>
								</label>
							</div>
							<div class="col-6">
								<label class="rounded px-2 py-2 d-block" style="cursor:pointer;font-size:11px;"
									:style="draft.populationAdjustmentMode === 'direct'
										? { border:'1px solid #f59e0b', background:'#fef3c7', color:'#78350f', fontWeight:500 }
										: { border:'1px solid #cbd5e1', color:'#64748b' }">
									<input type="radio" class="me-1" value="direct" v-model="draft.populationAdjustmentMode" />
									Direct trend
									<span class="d-block mt-1" style="font-size:10px;color:#64748b;line-height:1.35;font-weight:400;">
										Trend % × factor → proportional price shift.
									</span>
								</label>
							</div>
						</div>

						<div v-if="draft.populationAdjustmentMode === 'regional'" class="d-flex flex-column gap-3">
							<div>
								<label class="form-label mb-1" style="font-size:12px;font-weight:500;color:#334155;">Muni outgrows region (beats county AND state)</label>
								<div class="d-flex align-items-center gap-2">
									<input type="range" min="-20" max="20" step="0.5" v-model.number="draft.growthLeaderDisplay" class="form-range flex-grow-1" style="accent-color:#b45309;" />
									<input type="number" min="-20" max="20" step="0.5" v-model.number="draft.growthLeaderDisplay" class="form-control form-control-sm font-monospace text-end" style="width:80px;" />
									<span style="font-size:12px;color:#64748b;">%</span>
								</div>
							</div>
							<div>
								<label class="form-label mb-1" style="font-size:12px;font-weight:500;color:#334155;">Muni lags region (trails county AND state)</label>
								<div class="d-flex align-items-center gap-2">
									<input type="range" min="-25" max="10" step="0.5" v-model.number="draft.growthLaggardDisplay" class="form-range flex-grow-1" style="accent-color:#b45309;" />
									<input type="number" min="-25" max="10" step="0.5" v-model.number="draft.growthLaggardDisplay" class="form-control form-control-sm font-monospace text-end" style="width:80px;" />
									<span style="font-size:12px;color:#64748b;">%</span>
								</div>
							</div>
						</div>

						<div v-else>
							<div class="mb-3">
								<label class="form-label mb-1" style="font-size:12px;font-weight:500;color:#334155;">Decline factor</label>
								<div class="d-flex align-items-center gap-2">
									<input type="range" min="0" max="3" step="0.1" v-model.number="draft.populationDeclineFactor" class="form-range flex-grow-1" style="accent-color:#b45309;" />
									<input type="number" min="0" max="3" step="0.1" v-model.number="draft.populationDeclineFactor" class="form-control form-control-sm font-monospace text-end" style="width:64px;" />
									<span style="font-size:10px;color:#64748b;width:20px;">×</span>
								</div>
								<span style="font-size:10px;color:#64748b;">
									−10% pop × {{ draft.populationDeclineFactor }} = {{ (-10 * draft.populationDeclineFactor).toFixed(1) }}% on price.
								</span>
							</div>
							<div class="mb-3">
								<label class="form-label mb-1" style="font-size:12px;font-weight:500;color:#334155;">Growth factor</label>
								<div class="d-flex align-items-center gap-2">
									<input type="range" min="0" max="3" step="0.1" v-model.number="draft.populationGrowthFactor" class="form-range flex-grow-1" style="accent-color:#b45309;" />
									<input type="number" min="0" max="3" step="0.1" v-model.number="draft.populationGrowthFactor" class="form-control form-control-sm font-monospace text-end" style="width:64px;" />
									<span style="font-size:10px;color:#64748b;width:20px;">×</span>
								</div>
								<span style="font-size:10px;color:#64748b;">
									Set to 0 for no premium on growth (default). 1.0 mirrors decline behavior.
								</span>
							</div>
							<!-- Result preview mini-table -->
							<div class="rounded p-2" style="background:#f8fafc;font-size:10px;">
								<div class="fw-semibold mb-1" style="color:#64748b;">Result preview</div>
								<div class="row g-1 text-center font-monospace">
									<div v-for="t in [-0.10, -0.05, 0, 0.05, 0.10]" :key="t" class="col"
										:style="{ color: trendColor(t) }">
										<div style="color:#94a3b8;">{{ (t * 100).toFixed(0) }}%</div>
										<div class="fw-semibold">{{ trendResult(t) }}</div>
									</div>
								</div>
							</div>
						</div>
						<div v-if="impactPreview" class="pt-2 mt-2 d-flex align-items-baseline justify-content-between"
							style="border-top:1px solid #f1f5f9;font-size:11px;">
							<span style="color:#64748b;">Current impact <span style="color:#94a3b8;">· median muni 5yr trend −3.0%</span></span>
							<span class="font-monospace fw-semibold" :style="{ color: deltaColor(populationImpactCents) }">{{ fmtImpactSigned(populationImpactCents) }}</span>
						</div>
					</div>

					<!-- ④ SNAP -->
					<div class="rounded bg-white p-3 mb-3" style="border:1px solid #a7f3d0;">
						<div class="d-flex align-items-baseline justify-content-between gap-2 mb-2">
							<div class="d-flex align-items-baseline gap-2 min-w-0">
								<span class="rounded fw-semibold text-uppercase px-2 py-1" style="font-size:10px;letter-spacing:0.05em;background:#d1fae5;color:#065f46;">④</span>
								<h3 class="h6 fw-semibold mb-0 text-truncate">SNAP rate adjustment</h3>
							</div>
							<button v-if="draft.snapHighDisplay !== 0 || draft.snapLowDisplay !== 0" type="button" @click="zeroSnap"
								class="btn btn-link p-0" style="font-size:10px;color:#94a3b8;text-decoration:none;">Zero out</button>
						</div>
						<p class="mb-2" style="font-size:11px;color:#64748b;">How household food-assistance share moves price</p>
						<p class="rounded mb-2 px-2 py-2" style="font-size:12px;line-height:1.4;color:#334155;background:#f8fafc;">{{ snapSummary }}</p>
						<div class="row g-3">
							<div class="col-6">
								<div class="rounded p-2" style="border:1px solid #fecdd3;background:rgba(255,241,242,0.4);">
									<div class="fw-semibold mb-2" style="font-size:11px;color:#9f1239;">High SNAP band</div>
									<label class="d-block mb-2">
										<span class="d-block mb-1" style="font-size:10px;color:#64748b;">Threshold (≥ this → high)</span>
										<div class="d-flex align-items-center gap-1">
											<input type="number" min="0" max="30" step="0.5" v-model.number="draft.snapHighThresholdDisplay" class="form-control form-control-sm font-monospace text-end" />
											<span style="font-size:11px;color:#64748b;">%</span>
										</div>
									</label>
									<label class="d-block">
										<span class="d-block mb-1" style="font-size:10px;color:#64748b;">Adjustment</span>
										<div class="d-flex align-items-center gap-1">
											<input type="number" min="-25" max="10" step="0.5" v-model.number="draft.snapHighDisplay" class="form-control form-control-sm font-monospace text-end" />
											<span style="font-size:11px;color:#64748b;">%</span>
										</div>
									</label>
								</div>
							</div>
							<div class="col-6">
								<div class="rounded p-2" style="border:1px solid #a7f3d0;background:rgba(236,253,245,0.4);">
									<div class="fw-semibold mb-2" style="font-size:11px;color:#065f46;">Low SNAP band</div>
									<label class="d-block mb-2">
										<span class="d-block mb-1" style="font-size:10px;color:#64748b;">Threshold (≤ this → low)</span>
										<div class="d-flex align-items-center gap-1">
											<input type="number" min="0" max="30" step="0.5" v-model.number="draft.snapLowThresholdDisplay" class="form-control form-control-sm font-monospace text-end" />
											<span style="font-size:11px;color:#64748b;">%</span>
										</div>
									</label>
									<label class="d-block">
										<span class="d-block mb-1" style="font-size:10px;color:#64748b;">Adjustment</span>
										<div class="d-flex align-items-center gap-1">
											<input type="number" min="-10" max="25" step="0.5" v-model.number="draft.snapLowDisplay" class="form-control form-control-sm font-monospace text-end" />
											<span style="font-size:11px;color:#64748b;">%</span>
										</div>
									</label>
								</div>
							</div>
						</div>
						<div v-if="impactPreview" class="pt-2 mt-2 d-flex align-items-baseline justify-content-between"
							style="border-top:1px solid #f1f5f9;font-size:11px;">
							<span style="color:#64748b;">Current impact <span style="color:#94a3b8;">· median muni SNAP 10%</span></span>
							<span class="font-monospace fw-semibold" :style="{ color: deltaColor(snapImpactCents) }">{{ fmtImpactSigned(snapImpactCents) }}</span>
						</div>
					</div>

					<!-- ⑤ State tier -->
					<div class="rounded bg-white p-3" style="border:1px solid #a7f3d0;">
						<div class="d-flex align-items-baseline justify-content-between gap-2 mb-2">
							<div class="d-flex align-items-baseline gap-2 min-w-0">
								<span class="rounded fw-semibold text-uppercase px-2 py-1" style="font-size:10px;letter-spacing:0.05em;background:#d1fae5;color:#065f46;">⑤</span>
								<h3 class="h6 fw-semibold mb-0 text-truncate">State-tier adjustment</h3>
							</div>
							<button v-if="anyStateNonZero" type="button" @click="zeroStates"
								class="btn btn-link p-0" style="font-size:10px;color:#94a3b8;text-decoration:none;">Zero out</button>
						</div>
						<p class="mb-2" style="font-size:11px;color:#64748b;">Per-state premium or discount</p>
						<p class="rounded mb-2 px-2 py-2" style="font-size:12px;line-height:1.4;color:#334155;background:#f8fafc;">{{ stateSummary }}</p>
						<div>
							<button type="button" @click="stateMapOpen = !stateMapOpen"
								class="btn btn-link p-0" style="color:#92400e;font-size:12px;text-decoration:underline;">
								{{ stateMapOpen ? 'Hide' : 'Show' }} 50-state map editor
							</button>
						</div>
						<div v-if="stateMapOpen" class="mt-2">
							<input v-model="stateFilter" type="text" placeholder="Filter e.g. NY or WI…"
								class="form-control form-control-sm mb-2" />
							<div class="row g-2" style="max-height:24rem;overflow-y:auto;">
								<div v-for="s in filteredStates" :key="s" class="col-6">
									<div class="d-flex align-items-center gap-2" style="font-size:11px;">
										<span class="font-monospace fw-semibold" style="width:24px;color:#334155;">{{ s }}</span>
										<input type="number" min="-20" max="15" step="0.5" v-model.number="draft.stateAdjustDisplay[s]" class="form-control form-control-sm font-monospace text-end" />
										<span style="font-size:11px;color:#64748b;">%</span>
										<button v-if="defaults && draft.stateAdjustDisplay[s] !== defaults.stateAdjustDisplay[s]" type="button"
											@click="draft.stateAdjustDisplay[s] = defaults.stateAdjustDisplay[s]"
											class="btn btn-link p-0" style="color:#94a3b8;font-size:12px;width:20px;"
											:title="`Reset to ${defaults.stateAdjustDisplay[s]}%`">↺</button>
										<span v-else style="width:20px;"></span>
									</div>
								</div>
							</div>
							<p class="fst-italic mb-0 mt-2" style="font-size:10px;color:#64748b;">
								Changes apply immediately on save. Use the row-level ↺ to restore a single state's default.
							</p>
						</div>
						<div v-if="impactPreview" class="pt-2 mt-2 d-flex align-items-baseline justify-content-between"
							style="border-top:1px solid #f1f5f9;font-size:11px;">
							<span style="color:#64748b;">Current impact <span style="color:#94a3b8;">· median muni state WI</span></span>
							<span class="font-monospace fw-semibold" :style="{ color: deltaColor(stateImpactCents) }">{{ fmtImpactSigned(stateImpactCents) }}</span>
						</div>
					</div>

					<!-- Updated-by footer -->
					<div class="mt-3" style="font-size:11px;color:#64748b;">
						Last updated by: <strong>{{ settings.updated_by || '—' }}</strong>
						<span class="ms-3">at {{ formatDate(settings.updated_at) }}</span>
					</div>
				</div>
			</section>

			<!-- ─── Line-item pricing ────────────────────────────────────── -->
			<section class="rounded bg-white shadow-sm overflow-hidden mb-4" style="border:1px solid #e2e8f0;">
				<div class="px-3 py-2 d-flex align-items-baseline justify-content-between" style="background:#f8fafc;border-bottom:1px solid #e2e8f0;">
					<div>
						<h3 class="h6 fw-semibold mb-0">Line-item pricing</h3>
						<p class="mb-0 mt-1" style="font-size:11px;color:#64748b;">
							Override per-item amounts. State-tier / SNAP / trend adjustments apply to bundles only — line items stay at the values set here.
						</p>
					</div>
					<button type="button" @click="saveLineItems" :disabled="savingLineItems"
						class="btn btn-sm btn-primary fw-medium" style="font-size:12px;padding:6px 12px;">
						{{ savingLineItems ? 'Saving…' : 'Save line-item pricing' }}
					</button>
				</div>
				<div v-if="lineItemError" class="px-3 py-2" style="background:#fff1f2;border-bottom:1px solid #fecdd3;font-size:12px;color:#9f1239;">
					{{ lineItemError }}
				</div>
				<ul class="list-unstyled mb-0">
					<li v-for="item in config.lineItems" :key="item.id" class="px-3 py-2" style="border-top:1px solid #e2e8f0;">
						<div class="d-flex align-items-baseline justify-content-between gap-3">
							<button type="button" @click="openItemId = openItemId === item.id ? null : item.id"
								class="btn btn-link text-start flex-grow-1 d-flex align-items-baseline gap-2 p-0 text-decoration-none">
								<span style="font-size:10px;color:#94a3b8;">{{ openItemId === item.id ? '▾' : '▸' }}</span>
								<span class="fw-medium text-dark" style="font-size:14px;">{{ item.name }}</span>
								<span class="text-uppercase fw-semibold" style="font-size:10px;letter-spacing:0.05em;color:#64748b;">{{ item.category }}</span>
								<span v-if="lineItemDraft[item.id]" class="ms-auto fw-medium" style="font-size:10px;color:#92400e;">override active</span>
							</button>
						</div>
						<div v-if="openItemId === item.id" class="mt-2" style="padding-left:24px;">
							<div class="fst-italic mb-2" style="font-size:11px;color:#64748b;">{{ item.description }}</div>

							<!-- fixed-annual -->
							<template v-if="resolvedPricing(item).kind === 'fixed-annual'">
								<label class="d-flex align-items-center gap-2" style="font-size:12px;">
									<span style="width:112px;color:#64748b;">Annual ($)</span>
									<input type="number" step="0.01" min="0"
										:value="dollars(resolvedPricing(item).annualCents)"
										@input="setLineField(item.id, { kind: 'fixed-annual', annualCents: cents($event.target.value) })"
										class="form-control form-control-sm font-monospace text-end" style="width:128px;" />
								</label>
							</template>

							<!-- fixed-setup -->
							<template v-else-if="resolvedPricing(item).kind === 'fixed-setup'">
								<label class="d-flex align-items-center gap-2" style="font-size:12px;">
									<span style="width:112px;color:#64748b;">Setup ($)</span>
									<input type="number" step="0.01" min="0"
										:value="dollars(resolvedPricing(item).setupCents)"
										@input="setLineField(item.id, { kind: 'fixed-setup', setupCents: cents($event.target.value) })"
										class="form-control form-control-sm font-monospace text-end" style="width:128px;" />
								</label>
							</template>

							<!-- per-unit-annual -->
							<template v-else-if="resolvedPricing(item).kind === 'per-unit-annual'">
								<div class="row g-2">
									<div class="col-md-4">
										<label class="d-flex align-items-center gap-2" style="font-size:12px;">
											<span style="width:112px;color:#64748b;">Base ($/yr)</span>
											<input type="number" step="0.01" min="0"
												:value="dollars(resolvedPricing(item).baseAnnualCents || 0)"
												@input="setLineField(item.id, { kind: 'per-unit-annual', baseAnnualCents: cents($event.target.value) })"
												class="form-control form-control-sm font-monospace text-end flex-grow-1" />
										</label>
									</div>
									<div class="col-md-4">
										<label class="d-flex align-items-center gap-2" style="font-size:12px;">
											<span style="width:112px;color:#64748b;">Included qty</span>
											<input type="number" step="1" min="0"
												:value="resolvedPricing(item).includedQty"
												@input="setLineField(item.id, { kind: 'per-unit-annual', includedQty: Number($event.target.value) || 0 })"
												class="form-control form-control-sm font-monospace text-end flex-grow-1" />
										</label>
									</div>
									<div class="col-md-4">
										<label class="d-flex align-items-center gap-2" style="font-size:12px;">
											<span style="width:112px;color:#64748b;">Per-unit ($/yr)</span>
											<input type="number" step="0.01" min="0"
												:value="dollars(resolvedPricing(item).perUnitCents)"
												@input="setLineField(item.id, { kind: 'per-unit-annual', perUnitCents: cents($event.target.value) })"
												class="form-control form-control-sm font-monospace text-end flex-grow-1" />
										</label>
									</div>
								</div>
							</template>

							<!-- per-unit-setup -->
							<template v-else-if="resolvedPricing(item).kind === 'per-unit-setup'">
								<div class="row g-2">
									<div class="col-md-4">
										<label class="d-flex align-items-center gap-2" style="font-size:12px;">
											<span style="width:112px;color:#64748b;">Base ($)</span>
											<input type="number" step="0.01" min="0"
												:value="dollars(resolvedPricing(item).baseSetupCents || 0)"
												@input="setLineField(item.id, { kind: 'per-unit-setup', baseSetupCents: cents($event.target.value) })"
												class="form-control form-control-sm font-monospace text-end flex-grow-1" />
										</label>
									</div>
									<div class="col-md-4">
										<label class="d-flex align-items-center gap-2" style="font-size:12px;">
											<span style="width:112px;color:#64748b;">Included qty</span>
											<input type="number" step="1" min="0"
												:value="resolvedPricing(item).includedQty"
												@input="setLineField(item.id, { kind: 'per-unit-setup', includedQty: Number($event.target.value) || 0 })"
												class="form-control form-control-sm font-monospace text-end flex-grow-1" />
										</label>
									</div>
									<div class="col-md-4">
										<label class="d-flex align-items-center gap-2" style="font-size:12px;">
											<span style="width:112px;color:#64748b;">Per-unit ($)</span>
											<input type="number" step="0.01" min="0"
												:value="dollars(resolvedPricing(item).perUnitCents)"
												@input="setLineField(item.id, { kind: 'per-unit-setup', perUnitCents: cents($event.target.value) })"
												class="form-control form-control-sm font-monospace text-end flex-grow-1" />
										</label>
									</div>
								</div>
							</template>

							<!-- percent-of-bundle-annual -->
							<template v-else-if="resolvedPricing(item).kind === 'percent-of-bundle-annual'">
								<div class="row g-2">
									<div class="col-md-6">
										<label class="d-flex align-items-center gap-2" style="font-size:12px;">
											<span style="width:112px;color:#64748b;">% of hosting</span>
											<input type="number" step="1" min="0" max="200"
												:value="Math.round((resolvedPricing(item).pct || 0) * 100)"
												@input="setLineField(item.id, { kind: 'percent-of-bundle-annual', pct: (Number($event.target.value) || 0) / 100 })"
												class="form-control form-control-sm font-monospace text-end flex-grow-1" />
											<span style="font-size:10px;color:#64748b;">%</span>
										</label>
									</div>
									<div class="col-md-6">
										<label class="d-flex align-items-center gap-2" style="font-size:12px;">
											<span style="width:112px;color:#64748b;">Min floor ($/yr)</span>
											<input type="number" step="0.01" min="0"
												:value="dollars(resolvedPricing(item).minCents || 0)"
												@input="setLineField(item.id, { kind: 'percent-of-bundle-annual', minCents: cents($event.target.value) })"
												class="form-control form-control-sm font-monospace text-end flex-grow-1" />
										</label>
									</div>
								</div>
								<p class="fst-italic mb-0 mt-1" style="font-size:10px;color:#64748b;">
									Scales off the bundle's pure hosting annual (pre-adjustment, pre-support-surcharge). State-tier / SNAP / trend never touch this line.
								</p>
							</template>

							<!-- tier-annual -->
							<template v-else-if="resolvedPricing(item).kind === 'tier-annual'">
								<div class="d-flex flex-column gap-1">
									<div style="font-size:11px;color:#64748b;">Edit the price per tier. Up-to-qty and labels come from the catalog and aren't editable here.</div>
									<div v-for="(tier, i) in resolvedPricing(item).tiers" :key="i" class="d-flex align-items-center gap-2" style="font-size:12px;">
										<span class="font-monospace text-end" style="width:64px;color:#64748b;">≤ {{ Number(tier.upToQty).toLocaleString() }}</span>
										<span class="flex-grow-1" style="color:#334155;">{{ tier.label }}</span>
										<span style="color:#64748b;">$</span>
										<input type="number" step="0.01" min="0"
											:value="dollars(tier.annualCents)"
											@input="updateTier(item, i, 'annualCents', $event.target.value)"
											class="form-control form-control-sm font-monospace text-end" style="width:112px;" />
										<span style="color:#64748b;">/yr</span>
									</div>
								</div>
							</template>

							<!-- tier-setup -->
							<template v-else-if="resolvedPricing(item).kind === 'tier-setup'">
								<div class="d-flex flex-column gap-1">
									<div style="font-size:11px;color:#64748b;">Edit the one-time price per tier. Tier labels come from the catalog.</div>
									<div v-for="(tier, i) in resolvedPricing(item).tiers" :key="i" class="d-flex align-items-center gap-2" style="font-size:12px;">
										<span class="flex-grow-1" style="color:#334155;">{{ tier.label }}</span>
										<span style="color:#64748b;">$</span>
										<input type="number" step="0.01" min="0"
											:value="dollars(tier.setupCents)"
											@input="updateTier(item, i, 'setupCents', $event.target.value)"
											class="form-control form-control-sm font-monospace text-end" style="width:112px;" />
										<span style="color:#64748b;">one-time</span>
									</div>
								</div>
							</template>

							<div class="pt-2">
								<button type="button" @click="clearLineOverride(item.id)"
									:disabled="!lineItemDraft[item.id]"
									class="btn btn-link p-0" style="font-size:11px;color:#64748b;text-decoration:none;">
									↶ Reset this item to catalog default
								</button>
							</div>
						</div>
					</li>
				</ul>
			</section>
		</main>
	</div>
</template>

<script>
import restApi from '../../api/index.js'

const FACTORY_DEFAULTS = {
	populationAdjustmentMode: 'regional',
	populationDeclineFactor: 1.0,
	populationGrowthFactor: 0,
	growthLeaderDisplay: 5.0,
	growthLaggardDisplay: -5.0,
	snapHighThresholdDisplay: 12.0,
	snapHighDisplay: -5.0,
	snapLowThresholdDisplay: 8.0,
	snapLowDisplay: 3.0,
	setupFixedDollars: 456,
	setupPerPopCents: 14,
	annualFixedDollars: 684,
	annualPerPopCents: 22,
	baseMarkupDisplay: 0,
	stateAdjustDisplay: {},
	upgradeSetupMultiplier: 0.5,
	upgradeSetupMinDollars: 0,
	contractMultipliers: { 1: 1.0, 2: 0.97, 3: 0.93, 4: 0.90 },
}

export default {
	name: 'CwpeSettings',
	data() {
		return {
			loading: false,
			saving: false,
			savingLineItems: false,
			settings: null,
			config: null,
			draft: null,
			defaults: null,
			collapsed: false,
			status: null,
			statusTone: 'ok',
			stateMapOpen: false,
			stateFilter: '',
			lineItemDraft: {},
			openItemId: null,
			lineItemError: null,
			impactPreview: null,
			impactTimer: null,
		}
	},
	computed: {
		fmtPct() {
			return (d) => `${d >= 0 ? '+' : ''}${(d * 100).toFixed(1)}%`
		},
		fmtPctNoSign() {
			return (d) => `${(d * 100).toFixed(0)}%`
		},
		setupSummary() {
			if (!this.draft) return ''
			const fx = (this.draft.setupFixedDollars || 0).toLocaleString()
			const per = ((this.draft.setupPerPopCents || 0) / 100).toFixed(2)
			return `Charged once at signup. Formula: $${fx} fixed plus $${per} per resident, multiplied by the bundle tier.`
		},
		annualSummary() {
			if (!this.draft) return ''
			const fx = (this.draft.annualFixedDollars || 0).toLocaleString()
			const per = ((this.draft.annualPerPopCents || 0) / 100).toFixed(2)
			return `Recurring every year of service. Formula: $${fx} fixed plus $${per} per resident, multiplied by the bundle tier. Support surcharge applies to this, not setup.`
		},
		markupSummary() {
			if (!this.draft) return ''
			if (this.draft.baseMarkupDisplay === 0) return 'No global markup applied.'
			const sign = this.draft.baseMarkupDisplay > 0 ? '+' : ''
			return `Global markup lifts every setup and annual by ${sign}${this.draft.baseMarkupDisplay.toFixed(1)}%.`
		},
		populationSummary() {
			if (!this.draft) return ''
			if (this.draft.populationAdjustmentMode === 'direct') {
				const dec = this.draft.populationDeclineFactor
				const gr = this.draft.populationGrowthFactor
				const declineWord = dec === 0
					? 'no discount is applied'
					: dec === 1
						? 'the price drops by the same percentage'
						: `the price drops by ${dec}× that percentage`
				const growthWord = gr === 0
					? 'growth has no effect on price'
					: gr === 1
						? 'the price rises by the same percentage'
						: `the price rises by ${gr}× that percentage`
				return `Direct-trend mode: when a municipality's 5-year population declined, ${declineWord}. When it grew, ${growthWord}.`
			}
			const leader = `${this.draft.growthLeaderDisplay >= 0 ? '+' : ''}${this.draft.growthLeaderDisplay.toFixed(1)}%`
			const laggard = `${this.draft.growthLaggardDisplay >= 0 ? '+' : ''}${this.draft.growthLaggardDisplay.toFixed(1)}%`
			return `Regional mode: when the muni grows faster than BOTH its county and state, apply ${leader}. When it lags BOTH, apply ${laggard}. Otherwise no change.`
		},
		snapSummary() {
			if (!this.draft) return ''
			const high = this.draft.snapHighDisplay === 0 ? 'no change' : `${this.draft.snapHighDisplay >= 0 ? '+' : ''}${this.draft.snapHighDisplay.toFixed(1)}%`
			const low = this.draft.snapLowDisplay === 0 ? 'no change' : `${this.draft.snapLowDisplay >= 0 ? '+' : ''}${this.draft.snapLowDisplay.toFixed(1)}%`
			return `When SNAP rate is ≥ ${this.draft.snapHighThresholdDisplay.toFixed(0)}%, apply ${high}. When ≤ ${this.draft.snapLowThresholdDisplay.toFixed(0)}%, apply ${low}. In between is neutral.`
		},
		stateSummary() {
			if (!this.draft) return ''
			const entries = Object.entries(this.draft.stateAdjustDisplay || {})
			const nonZero = entries.filter(([, v]) => v !== 0)
			if (nonZero.length === 0) return 'All states are neutral — state of the muni has no effect on pricing.'
			const premium = nonZero.filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1])
			const discount = nonZero.filter(([, v]) => v < 0).sort((a, b) => a[1] - b[1])
			const fmt = (arr) => arr.slice(0, 3).map(([s, v]) => `${s} ${v >= 0 ? '+' : ''}${v.toFixed(1)}%`).join(', ')
			return `${nonZero.length} states have an adjustment. Biggest premiums: ${fmt(premium) || '—'}. Biggest discounts: ${fmt(discount) || '—'}.`
		},
		wholeFormulaSummary() {
			return `${this.setupSummary} ${this.annualSummary} ${this.markupSummary} ${this.populationSummary} ${this.snapSummary} ${this.stateSummary}`
		},
		filteredStates() {
			if (!this.draft) return []
			const filt = this.stateFilter.trim().toUpperCase()
			return Object.keys(this.draft.stateAdjustDisplay).sort().filter(s => !filt || s.includes(filt))
		},
		canZeroPopulation() {
			if (!this.draft) return false
			return this.draft.populationAdjustmentMode === 'direct'
				? (this.draft.populationDeclineFactor !== 0 || this.draft.populationGrowthFactor !== 0)
				: (this.draft.growthLeaderDisplay !== 0 || this.draft.growthLaggardDisplay !== 0)
		},
		anyStateNonZero() {
			if (!this.draft) return false
			return Object.values(this.draft.stateAdjustDisplay).some(v => v !== 0)
		},
		populationImpactCents() {
			// pop-decline / pop-growth (direct mode) or growth-leader / growth-laggard (regional mode)
			return (this.factorImpactCents('pop-decline') || 0)
				+ (this.factorImpactCents('pop-growth') || 0)
				+ (this.factorImpactCents('growth-leader') || 0)
				+ (this.factorImpactCents('growth-laggard') || 0)
		},
		snapImpactCents() {
			return (this.factorImpactCents('snap-high') || 0) + (this.factorImpactCents('snap-low') || 0)
		},
		stateImpactCents() {
			return this.factorImpactCents('state-tier') || 0
		},
	},
	created() {
		this.load()
	},
	beforeDestroy() {
		if (this.impactTimer) clearTimeout(this.impactTimer)
	},
	watch: {
		// Any change to the draft schedules a simulate against a median muni
		// to refresh the per-factor impact $ display. Debounced 350ms so a
		// slider drag doesn't fire 50 requests.
		draft: {
			handler() {
				if (this.impactTimer) clearTimeout(this.impactTimer)
				this.impactTimer = setTimeout(this.refreshImpact, 350)
			},
			deep: true,
		},
	},
	methods: {
		load() {
			this.loading = true
			Promise.all([
				restApi.get('/cwpe/admin/pricing-settings'),
				restApi.get('/cwpe/pricing/config'),
			]).then(([settingsRes, configRes]) => {
				this.settings = settingsRes.data
				this.config = configRes.data
				this.draft = this.settingsToDraft(settingsRes.data)
				this.defaults = { ...FACTORY_DEFAULTS, stateAdjustDisplay: { ...this.draft.stateAdjustDisplay } }
				this.lineItemDraft = JSON.parse(JSON.stringify(this.config.settings?.line_item_overrides || {}))
				this.refreshImpact()
			}).catch(err => {
				this.$swal.fire({ icon: 'error', title: 'Access denied or failed', text: err?.response?.data?.message || '' })
			}).finally(() => { this.loading = false })
		},
		// Live impact preview — POSTs draft state to /cwpe/pricing/simulate so
		// the factor cards can show the $ delta the current knobs would apply
		// to a median 5,000-pop muni. Saved settings aren't required: the
		// simulate endpoint takes inputs ad-hoc.
		refreshImpact() {
			if (!this.draft) { this.impactPreview = null; return }
			restApi.post('/cwpe/pricing/simulate', {
				population: 5000,
				population_trend_pct: -0.03,
				snap_rate: 0.10,
				state: 'WI',
				support_tier_id: 'standard',
			}).then(({ data }) => {
				this.impactPreview = data?.bundles?.[0] || null
			}).catch(() => { this.impactPreview = null })
		},
		factorImpactCents(key) {
			const b = this.impactPreview
			if (!b) return null
			const f = (b.adjustmentFactors || []).find(x => x.key === key)
			if (!f) return 0
			const baseLine = (b.baseSetupCents || 0) + (b.baseAnnualCents || 0)
			return Math.round(baseLine * f.deltaPct)
		},
		settingsToDraft(s) {
			return {
				populationAdjustmentMode: s.population_adjustment_mode || 'regional',
				populationDeclineFactor: s.population_decline_factor ?? 1.0,
				populationGrowthFactor: s.population_growth_factor ?? 0,
				growthLeaderDisplay: (s.growth_leader_pct || 0) * 100,
				growthLaggardDisplay: (s.growth_laggard_pct || 0) * 100,
				snapHighThresholdDisplay: (s.snap_high_threshold || 0) * 100,
				snapHighDisplay: (s.snap_high_pct || 0) * 100,
				snapLowThresholdDisplay: (s.snap_low_threshold || 0) * 100,
				snapLowDisplay: (s.snap_low_pct || 0) * 100,
				setupFixedDollars: (s.setup_fixed_cents || 0) / 100,
				setupPerPopCents: s.setup_per_pop_cents || 0,
				annualFixedDollars: (s.annual_fixed_cents || 0) / 100,
				annualPerPopCents: s.annual_per_pop_cents || 0,
				baseMarkupDisplay: (s.base_markup_pct || 0) * 100,
				stateAdjustDisplay: Object.fromEntries(
					Object.entries(s.state_adjust || {}).map(([k, v]) => [k, (v || 0) * 100])
				),
				upgradeSetupMultiplier: s.upgrade_setup_multiplier ?? 0.5,
				upgradeSetupMinDollars: (s.upgrade_setup_min_cents || 0) / 100,
				contractMultipliers: { ...(s.contract_term_multipliers || { 1: 1.0, 2: 0.97, 3: 0.93, 4: 0.90 }) },
			}
		},
		draftToPayload() {
			return {
				population_adjustment_mode: this.draft.populationAdjustmentMode,
				population_decline_factor: this.draft.populationDeclineFactor,
				population_growth_factor: this.draft.populationGrowthFactor,
				growth_leader_pct: this.draft.growthLeaderDisplay / 100,
				growth_laggard_pct: this.draft.growthLaggardDisplay / 100,
				snap_high_threshold: this.draft.snapHighThresholdDisplay / 100,
				snap_high_pct: this.draft.snapHighDisplay / 100,
				snap_low_threshold: this.draft.snapLowThresholdDisplay / 100,
				snap_low_pct: this.draft.snapLowDisplay / 100,
				setup_fixed_cents: Math.round(this.draft.setupFixedDollars * 100),
				setup_per_pop_cents: this.draft.setupPerPopCents,
				annual_fixed_cents: Math.round(this.draft.annualFixedDollars * 100),
				annual_per_pop_cents: this.draft.annualPerPopCents,
				base_markup_pct: this.draft.baseMarkupDisplay / 100,
				state_adjust: Object.fromEntries(
					Object.entries(this.draft.stateAdjustDisplay).map(([k, v]) => [k, (v || 0) / 100])
				),
				upgrade_setup_multiplier: this.draft.upgradeSetupMultiplier,
				upgrade_setup_min_cents: Math.round(this.draft.upgradeSetupMinDollars * 100),
				contract_term_multipliers: this.draft.contractMultipliers,
			}
		},
		onSave() {
			this.saving = true
			this.status = null
			restApi.put('/cwpe/admin/pricing-settings', this.draftToPayload()).then(({ data }) => {
				this.settings = { ...this.settings, ...data }
				this.statusTone = 'ok'
				this.status = 'Saved'
				setTimeout(() => { this.status = null }, 3000)
			}).catch(err => {
				this.statusTone = 'err'
				this.status = err?.response?.data?.message || 'Save failed'
				setTimeout(() => { this.status = null }, 4000)
			}).finally(() => { this.saving = false })
		},
		onReset() {
			this.$swal.fire({
				title: 'Reset every knob to defaults?',
				text: 'This affects quotes immediately.',
				icon: 'warning',
				showCancelButton: true,
				confirmButtonText: 'Reset',
				confirmButtonColor: '#dc3545',
			}).then(result => {
				if (!result.isConfirmed) return
				this.saving = true
				restApi.post('/cwpe/admin/pricing-settings/reset').then(() => {
					this.load()
					this.statusTone = 'ok'
					this.status = 'Reset to defaults'
					setTimeout(() => { this.status = null }, 3000)
				}).catch(err => {
					this.statusTone = 'err'
					this.status = err?.response?.data?.message || 'Reset failed'
				}).finally(() => { this.saving = false })
			})
		},
		zeroPopulation() {
			if (this.draft.populationAdjustmentMode === 'direct') {
				this.draft.populationDeclineFactor = 0
				this.draft.populationGrowthFactor = 0
			} else {
				this.draft.growthLeaderDisplay = 0
				this.draft.growthLaggardDisplay = 0
			}
		},
		zeroSnap() {
			this.draft.snapHighDisplay = 0
			this.draft.snapLowDisplay = 0
		},
		zeroStates() {
			Object.keys(this.draft.stateAdjustDisplay).forEach(k => {
				this.$set(this.draft.stateAdjustDisplay, k, 0)
			})
		},
		// ── Line items ─────────────────────────────────────────────────
		resolvedPricing(item) {
			return { ...item.pricing, ...(this.lineItemDraft[item.id] || {}) }
		},
		setLineField(id, patch) {
			this.$set(this.lineItemDraft, id, { ...(this.lineItemDraft[id] || {}), ...patch })
		},
		clearLineOverride(id) {
			this.$delete(this.lineItemDraft, id)
		},
		updateTier(item, idx, field, rawValue) {
			const current = this.resolvedPricing(item)
			const cents = field === 'annualCents' || field === 'setupCents' ? this.cents(rawValue) : Number(rawValue)
			const nextTiers = current.tiers.map((t, j) => j === idx ? { ...t, [field]: cents } : t)
			this.setLineField(item.id, { kind: current.kind, tiers: nextTiers })
		},
		saveLineItems() {
			this.savingLineItems = true
			this.lineItemError = null
			restApi.put('/cwpe/admin/line-item-overrides', { overrides: this.lineItemDraft }).then(({ data }) => {
				const updated = (data && data.line_item_overrides) || this.lineItemDraft
				this.lineItemDraft = JSON.parse(JSON.stringify(updated))
				this.$swal.fire({ icon: 'success', title: 'Line-item pricing saved', timer: 1200, showConfirmButton: false })
			}).catch(err => {
				this.lineItemError = err?.response?.data?.message || 'Save failed'
			}).finally(() => { this.savingLineItems = false })
		},
		dollars(c) { return (c || 0) / 100 },
		cents(d) { return Math.round(Number(d || 0) * 100) },
		fmtImpact(c) {
			if (c == null) return '—'
			return '$' + Math.round(c / 100).toLocaleString('en-US')
		},
		fmtImpactSigned(c) {
			if (c == null || c === 0) return '$0'
			const sign = c > 0 ? '+' : '−'
			return sign + '$' + Math.round(Math.abs(c) / 100).toLocaleString('en-US')
		},
		deltaColor(c) {
			if (c > 0) return '#047857'
			if (c < 0) return '#be123c'
			return '#64748b'
		},
		trendColor(t) {
			if (t < 0) return (t * this.draft.populationDeclineFactor) !== 0 ? '#be123c' : '#64748b'
			if (t > 0) return (t * this.draft.populationGrowthFactor) !== 0 ? '#047857' : '#64748b'
			return '#64748b'
		},
		trendResult(t) {
			if (t < 0) return `${(t * this.draft.populationDeclineFactor * 100).toFixed(1)}%`
			if (t > 0) {
				const sign = this.draft.populationGrowthFactor ? '+' : ''
				return `${sign}${(t * this.draft.populationGrowthFactor * 100).toFixed(1)}%`
			}
			return '0.0%'
		},
		formatDate(val) {
			if (!val) return '—'
			return new Date(val).toLocaleString()
		},
	},
}
</script>
